import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { forwardRef, type ReactNode } from "react";
import { X } from "lucide-react";
import type { Variants } from "motion/react";
import { motion, useIsPresent } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;
export const AlertDialogCancel = AlertDialogPrimitive.Cancel;
export const AlertDialogAction = AlertDialogPrimitive.Action;

/** Full-viewport flex positioner — motion must not fight translate-based centering. */
export const alertDialogPositionerClass =
  "group fixed inset-0 z-[var(--z-alert)] flex w-auto max-h-none items-center justify-center overflow-y-auto overflow-x-hidden border-0 bg-transparent p-4 shadow-none outline-none sm:p-6";

export const alertDialogPanelClass =
  "relative grid w-[min(90vw,28rem)] max-h-[min(85dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem))] gap-4 overflow-auto rounded-lg border border-border bg-surface-elevated p-6 shadow-md group-data-[state=open]:animate-dialog-in-mauto group-data-[state=closed]:animate-dialog-out-mauto motion-reduce:animate-none motion-reduce:transition-none";

export const AlertDialogOverlay = forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[var(--z-alert-overlay)] bg-[rgb(16_24_40_/_0.5)] backdrop-blur-[10px] animate-overlay-in data-[state=closed]:animate-overlay-out",
      className,
    )}
    {...props}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

export const AlertDialogContent = forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Portal container={typeof document !== "undefined" ? document.body : undefined}>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={alertDialogPositionerClass}
      data-testid="alert-dialog-positioner"
      {...props}
    >
      <div className={cn(alertDialogPanelClass, className)} data-testid="alert-dialog-panel">
        {children}
      </div>
    </AlertDialogPrimitive.Content>
  </AlertDialogPrimitive.Portal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

/**
 * `AlertDialogContent` animated by Motion instead of CSS keyframes. Render it
 * inside `AnimatePresence` only while open: Radix stays mounted through the
 * panel's `exit` variant, and the panel is inert while it exits.
 */
export const AlertDialogMotionContent = forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & { panelVariants: Variants }
>(({ className, children, panelVariants, ...props }, ref) => {
  const isPresent = useIsPresent();
  return (
    <AlertDialogPrimitive.Portal
      forceMount
      container={typeof document !== "undefined" ? document.body : undefined}
    >
      <AlertDialogOverlay forceMount />
      <AlertDialogPrimitive.Content
        ref={ref}
        forceMount
        className={alertDialogPositionerClass}
        data-testid="alert-dialog-positioner"
        {...props}
      >
        <motion.div
          className={cn(
            alertDialogPanelClass,
            "group-data-[state=open]:animate-none group-data-[state=closed]:animate-none",
            className,
          )}
          data-testid="alert-dialog-panel"
          variants={panelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          inert={!isPresent}
        >
          {children}
        </motion.div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});
AlertDialogMotionContent.displayName = "AlertDialogMotionContent";

/** Alert dialog above modal popovers (e.g. nested confirmation inside Save Contact). */
export const AlertDialogElevatedContent = forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Portal>
    <AlertDialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-[var(--z-nested-alert-overlay)] bg-[rgb(16_24_40_/_0.45)] animate-overlay-in data-[state=closed]:animate-overlay-out",
      )}
    />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "group fixed inset-0 z-[var(--z-nested-alert)] flex w-auto max-h-none items-center justify-center overflow-y-auto overflow-x-hidden border-0 bg-transparent p-4 shadow-none outline-none sm:p-6",
      )}
      {...props}
    >
      <div className={cn(alertDialogPanelClass, "z-[var(--z-nested-alert)]", className)}>
        {children}
      </div>
    </AlertDialogPrimitive.Content>
  </AlertDialogPrimitive.Portal>
));
AlertDialogElevatedContent.displayName = "AlertDialogElevatedContent";

export function AlertDialogTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <AlertDialogPrimitive.Title
      className={cn("m-0 text-[1.1rem] font-bold tracking-[-0.01em]", className)}
    >
      {children}
    </AlertDialogPrimitive.Title>
  );
}

export function AlertDialogDescription({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <AlertDialogPrimitive.Description
      className={cn("m-0 leading-relaxed text-muted", className)}
    >
      {children}
    </AlertDialogPrimitive.Description>
  );
}

export function AlertDialogActions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={cn("flex min-h-10 flex-wrap items-center justify-end gap-3", className)}>
      {children}
    </div>
  );
}

/** Icon close control for dismissible product dialogs built on AlertDialog. */
export function AlertDialogCloseButton({
  label,
  onClose,
  disabled = false,
  className,
}: {
  label: string;
  onClose: () => void;
  disabled?: boolean;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        "absolute end-3 top-3 inline-flex size-8 items-center justify-center rounded-md border-0 bg-transparent text-muted transition-colors hover:bg-surface-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)] disabled:opacity-50",
        className,
      )}
      aria-label={label}
      data-testid="alert-dialog-close"
      disabled={disabled}
      onClick={onClose}
    >
      <X size={16} aria-hidden="true" />
    </button>
  );
}
